// YouTube catalog adapter.
// Takes the raw youtube.json (YouTube Data API v3 shape) + video-groups.json
// (editorial overlay) and returns typed, normalised, grouped video data.
//
// Pure functions — no React, no DOM. Tests import these directly.

import {
  RawYouTubeVideosSchema,
  VideoGroupsMapSchema,
  type RawYouTubeVideo,
  type Video,
  type VideoGroupsMap,
} from '@lib/schemas/youtube';

// ---------------------------------------------------------------------------
// Duration helpers
// ---------------------------------------------------------------------------

/** Parse ISO 8601 duration "PT1H32M5S" → seconds. */
export function parseIsoDuration(iso: string): number {
  const m = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(iso);
  if (!m) return 0;
  const h = parseInt(m[1] ?? '0', 10);
  const min = parseInt(m[2] ?? '0', 10);
  const s = parseInt(m[3] ?? '0', 10);
  return h * 3600 + min * 60 + s;
}

/** Seconds → "MM:SS" or "H:MM:SS". */
export function formatDurationLabel(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${m}:${pad(s)}`;
}

// ---------------------------------------------------------------------------
// Normalise a raw video to the flat shape the UI consumes.
// ---------------------------------------------------------------------------

export function normaliseVideo(raw: RawYouTubeVideo): Video {
  const seconds = parseIsoDuration(raw.contentDetails?.duration ?? 'PT0S');
  const publishedAt = raw.snippet.publishedAt;
  const year = publishedAt.slice(0, 4);

  // Pick best YouTube thumbnail URL for downstream override fallback. We do
  // NOT default to using this as the visible tile image — the tile component
  // decides. We just expose it so it's available if needed.
  const t = raw.snippet.thumbnails;
  const thumbnailUrl =
    t?.maxres?.url ?? t?.high?.url ?? t?.medium?.url ?? t?.default?.url;

  return {
    videoId: raw.id,
    title: raw.snippet.title,
    description: raw.snippet.description,
    channelTitle: raw.snippet.channelTitle,
    channelId: raw.snippet.channelId,
    publishedAt,
    year,
    duration: raw.contentDetails?.duration ?? 'PT0S',
    durationLabel: formatDurationLabel(seconds),
    durationSeconds: seconds,
    viewCount: parseInt(raw.statistics?.viewCount ?? '0', 10),
    likeCount: parseInt(raw.statistics?.likeCount ?? '0', 10),
    tags: raw.snippet.tags,
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
    url: `https://www.youtube.com/watch?v=${raw.id}`,
  };
}

// ---------------------------------------------------------------------------
// Catalog load + validation
// ---------------------------------------------------------------------------

/**
 * Validate + normalise the raw YouTube data + grouping map.
 * Throws on schema failure (called at module load).
 */
export function buildCatalog(
  rawVideos: unknown,
  rawGroups: unknown
): { videos: Video[]; groups: VideoGroupsMap } {
  const parsedVideos = RawYouTubeVideosSchema.parse(rawVideos);
  const parsedGroups = VideoGroupsMapSchema.parse(rawGroups);
  const videos = parsedVideos.map(normaliseVideo);
  return { videos, groups: parsedGroups };
}

// ---------------------------------------------------------------------------
// Grouping / lookup
// ---------------------------------------------------------------------------

export interface VideoGroup {
  title: string;
  videos: ReadonlyArray<Video>;
  /** Optional custom thumbnail per video (videoId → src). */
  customThumbnails: Readonly<Record<string, string>>;
}

/**
 * Split videos into ordered groups based on the editorial map. Videos with no
 * explicit assignment fall into `defaultGroup`. Within each group, sorted
 * by published date descending (newest first).
 */
export function groupVideos(
  videos: ReadonlyArray<Video>,
  map: VideoGroupsMap
): VideoGroup[] {
  const byGroup = new Map<string, Video[]>();
  for (const title of map.order) byGroup.set(title, []);

  const thumbnails: Record<string, Record<string, string>> = {};

  for (const v of videos) {
    const a = map.assignments[v.videoId];
    const group = a?.group ?? map.defaultGroup;
    // Initialise lazily in case a video assigns itself to a group not in `order`
    if (!byGroup.has(group)) byGroup.set(group, []);
    byGroup.get(group)!.push(v);
    if (a?.customThumbnail) {
      thumbnails[group] = thumbnails[group] ?? {};
      thumbnails[group][v.videoId] = a.customThumbnail;
    }
  }

  // Sort each group's videos newest first
  for (const arr of byGroup.values()) {
    arr.sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
  }

  return map.order
    .map((title) => ({
      title,
      videos: byGroup.get(title) ?? [],
      customThumbnails: thumbnails[title] ?? {},
    }))
    .filter((g) => g.videos.length > 0);
}

// ---------------------------------------------------------------------------
// Convenience accessors (used by tests + future filtering UIs)
// ---------------------------------------------------------------------------

export function videosByYear(videos: ReadonlyArray<Video>, year: string): Video[] {
  return videos.filter((v) => v.year === year);
}

export function videosByChannel(
  videos: ReadonlyArray<Video>,
  channelTitle: string
): Video[] {
  return videos.filter((v) => v.channelTitle === channelTitle);
}

export function topVideosByViews(
  videos: ReadonlyArray<Video>,
  limit = 10
): Video[] {
  return [...videos].sort((a, b) => b.viewCount - a.viewCount).slice(0, limit);
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

export interface PageResult<T> {
  items: ReadonlyArray<T>;
  page: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
}

export function paginate<T>(
  items: ReadonlyArray<T>,
  page: number,
  pageSize: number
): PageResult<T> {
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize),
    page: safePage,
    pageSize,
    totalPages,
    totalItems,
  };
}
